import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FiCheckCircle, FiXCircle, FiCalendar, FiClock, FiX, FiCopy, FiCheck } from 'react-icons/fi';
import { Tooltip } from 'antd';
import {
    DAYS, DAY_STATUS, describeScheduleDay, rangeKey, countWeekday, isRangeExpired,
    formatDate, formatTime12, formatTimeShort, formatTimeRange, toMinutes
} from './helpers';
import { SwitchField, TimeField, DateField } from './fields';
import './weeklySchedule.css';

// The four choices, worded for people who have never seen the page before.
const OPTIONS = [
    { value: DAY_STATUS.NORMAL, icon: <FiCheckCircle />, title: 'Open all day', hint: 'All slots can be booked' },
    { value: DAY_STATUS.OFF, icon: <FiXCircle />, title: 'Closed every week', hint: 'No slots on this day' },
    { value: DAY_STATUS.CUSTOM_OFF, icon: <FiCalendar />, title: 'Closed for some dates', hint: 'Closed between two dates' },
    { value: DAY_STATUS.CUSTOM, icon: <FiClock />, title: 'Open at set times', hint: 'Opening, closing and a break' }
];

const STATUS_BADGE = {
    [DAY_STATUS.NORMAL]: { pill: 'is-open', text: 'Open' },
    [DAY_STATUS.OFF]: { pill: 'is-off', text: 'Closed' },
    [DAY_STATUS.CUSTOM_OFF]: { pill: 'is-holiday', text: 'Dates off' },
    [DAY_STATUS.CUSTOM]: { pill: 'is-custom', text: 'Set times' },
    [DAY_STATUS.INHERIT]: { pill: 'is-primary', text: 'Same as General' }
};

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const isPastDate = (date) => date && date.isBefore(dayjs(), 'day');

// The rule that really applies to a row: its own, or General's when it follows General.
const effectiveRule = (row, general) => (row.status === DAY_STATUS.INHERIT ? { ...general, day: row.day } : row);

// Short status for the week strip tiles.
const tileText = (rule) => {
    switch (rule.status) {
        case DAY_STATUS.OFF: return 'Closed';
        case DAY_STATUS.CUSTOM_OFF: return rule.to ? `Off till ${dayjs(rule.to).format('D MMM')}` : 'Pick dates';
        case DAY_STATUS.CUSTOM: return rule.open && rule.close ? `${formatTimeShort(rule.open)} – ${formatTimeShort(rule.close)}` : 'Pick times';
        default: return 'Open all day';
    }
};

// One plain sentence telling the admin what customers will get on this day.
const summaryFor = (row, general) => {
    const { day } = row;
    switch (row.status) {
        case DAY_STATUS.INHERIT:
            return `Same as General: ${general.kind === 'off' ? `closed every ${day}` : general.kind === 'custom' ? `open ${general.text}` : 'open all day'}.`;
        case DAY_STATUS.OFF:
            return `Closed every ${day}. Customers cannot book this day.`;
        case DAY_STATUS.CUSTOM_OFF: {
            if (!row.from || !row.to) return 'Pick the dates the center is closed.';
            const count = countWeekday(row.from, row.to, day);
            return `Closed on ${day}s from ${formatDate(row.from)} to ${formatDate(row.to)}${count ? ` (${count} ${day}${count > 1 ? 's' : ''})` : ''}. Open as normal on other ${day}s.`;
        }
        case DAY_STATUS.CUSTOM:
            if (!row.open || !row.close) return 'Pick the opening and closing time.';
            if (row.hasBreak && row.breakFrom && row.breakTo) {
                return `Customers can book ${day} from ${formatTime12(row.open)} to ${formatTime12(row.close)}, except the break from ${formatTime12(row.breakFrom)} to ${formatTime12(row.breakTo)}, every week.`;
            }
            return `Customers can book ${day} from ${formatTime12(row.open)} to ${formatTime12(row.close)}, every week.`;
        default:
            return `Customers can book all slots every ${day}.`;
    }
};

const rangeLabel = (range) => {
    const dates = `${formatDate(range.from)} – ${formatDate(range.to)}`;
    if (range.status === DAY_STATUS.OFF) return `Closed · ${dates}`;
    if (range.status === DAY_STATUS.CUSTOM) return `${formatTimeRange(range.open, range.close)} · ${dates}`;
    return `Open · ${dates}`;
};

const pct = (minutes) => `${Math.min(Math.max(minutes / 1440, 0), 1) * 100}%`;

// 24-hour bar: green = bookable, grey = closed, striped red = closed for dates, gap = break.
const DayTimeline = ({ rule, large = false }) => {
    let segments = [];
    if (rule.status === DAY_STATUS.CUSTOM) {
        const open = toMinutes(rule.open);
        const close = toMinutes(rule.close);
        if (open !== null && close !== null && close > open) {
            const breakFrom = rule.hasBreak ? toMinutes(rule.breakFrom) : null;
            const breakTo = rule.hasBreak ? toMinutes(rule.breakTo) : null;
            segments = breakFrom !== null && breakTo !== null && breakFrom > open && breakTo < close && breakTo > breakFrom
                ? [[open, breakFrom], [breakTo, close]]
                : [[open, close]];
        }
    }
    const fill = rule.status === DAY_STATUS.NORMAL ? 'is-open' : rule.status === DAY_STATUS.CUSTOM_OFF ? 'is-dates' : '';
    return (
        <div className={`ss-tl ${large ? 'is-large' : ''}`} aria-hidden="true">
            <div className={`ss-tl-track ${fill}`}>
                {segments.map(([from, to]) => (
                    <span key={from} className="ss-tl-seg" style={{ left: pct(from), width: `calc(${pct(to)} - ${pct(from)})` }} />
                ))}
            </div>
            {large && (
                <div className="ss-tl-ticks">
                    {['12 AM', '6 AM', '12 PM', '6 PM', '12 AM'].map((label, index) => <span key={index}>{label}</span>)}
                </div>
            )}
        </div>
    );
};

// Weekly schedule: a week strip to see every day at a glance, and one editor for the selected day.
// Older date rules the editor can't show are kept as removable chips, so saving never deletes them.
// isCenter: adds a "Same as General" switch; generalSchedule feeds what General says for each day.
// errors: { [day]: message, [day#rangeId]: message }
const WeeklyScheduleEditor = ({ rows, onChange, isCenter = false, generalSchedule = [], errors = {}, disabled = false }) => {
    const prefix = isCenter ? 'c' : 'g';
    const [selected, setSelected] = useState(DAYS[0]);
    const [copyTargets, setCopyTargets] = useState([]);
    const [copiedTo, setCopiedTo] = useState([]);

    const dayHasError = (row) => !!errors[row.day] || (row.ranges || []).some(range => errors[rangeKey(row.day, range)]);

    // After a failed save, open the first day that needs fixing.
    useEffect(() => {
        const current = rows.find(row => row.day === selected);
        if (current && dayHasError(current)) return;
        const firstWithError = rows.find(dayHasError);
        if (firstWithError) setSelected(firstWithError.day);
        // eslint-disable-next-line
    }, [errors]);

    useEffect(() => {
        setCopyTargets([]);
        setCopiedTo([]);
    }, [selected]);

    const updateRow = (day, changes) => {
        onChange(rows.map(row => (row.day === day ? { ...row, ...changes } : row)));
    };

    const removeRange = (row, id) => updateRow(row.day, { ranges: row.ranges.filter(range => range.id !== id) });

    const row = rows.find(r => r.day === selected) || rows[0];
    if (!row) return null;
    const general = describeScheduleDay(generalSchedule, row.day);
    const inherit = row.status === DAY_STATUS.INHERIT;
    const error = errors[row.day];
    const ranges = row.ranges || [];
    const id = `${prefix}-${row.day}`;
    const badge = STATUS_BADGE[row.status] || STATUS_BADGE[DAY_STATUS.NORMAL];

    const toggleTarget = (day) => setCopyTargets(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));

    const applyCopy = () => {
        const { status, open, close, hasBreak, breakFrom, breakTo, from, to } = row;
        onChange(rows.map(r => (copyTargets.includes(r.day) ? { ...r, status, open, close, hasBreak, breakFrom, breakTo, from, to } : r)));
        setCopiedTo(copyTargets);
        setCopyTargets([]);
    };

    return (
        <div className="ss-wk">
            {/* Week at a glance */}
            <div className="ss-wk-strip" role="tablist" aria-label="Days of the week">
                {rows.map(r => {
                    const rule = effectiveRule(r, describeScheduleDay(generalSchedule, r.day));
                    const active = r.day === row.day;
                    return (
                        <button key={r.day} type="button" role="tab" aria-selected={active}
                            className={`ss-wk-tile is-${rule.status} ${active ? 'active' : ''} ${dayHasError(r) ? 'has-error' : ''}`}
                            onClick={() => setSelected(r.day)}>
                            <span className="ss-wk-tile-top">
                                <span className="ss-wk-tile-day">{r.day.substring(0, 3)}</span>
                                {r.status === DAY_STATUS.INHERIT && <span className="ss-wk-tile-tag">General</span>}
                                {dayHasError(r) && <span className="ss-wk-tile-dot" title="Needs attention" />}
                            </span>
                            <span className="ss-wk-tile-text">{tileText(rule)}</span>
                            <DayTimeline rule={rule} />
                        </button>
                    );
                })}
            </div>

            {/* Editor for the selected day */}
            <div className={`ss-wk-panel ${error ? 'has-error' : ''}`} role="tabpanel" aria-label={`${row.day} schedule`}>
                <div className="ss-wk-panel-head">
                    <div>
                        <h6 className="ss-wk-panel-title">{row.day}</h6>
                        <span className="ss-wk-panel-sub">How should {row.day} work{isCenter ? ' at this center' : ''}?</span>
                    </div>
                    <span className={`ss-pill ${badge.pill}`}>{badge.text}</span>
                </div>

                {isCenter && (
                    <div className="ss-wk-inherit">
                        <SwitchField id={`${id}-inherit`} label="Same as General" checked={inherit} disabled={disabled}
                            onChange={checked => updateRow(row.day, { status: checked ? DAY_STATUS.INHERIT : DAY_STATUS.NORMAL })} />
                        <span className="ss-day-general">
                            General says:
                            <Tooltip title={general.ranges ? `General also has ${general.ranges} date-based rule(s) for this day.` : ''}>
                                <span className={`ss-pill ${general.kind === 'off' ? 'is-off' : general.kind === 'custom' ? 'is-custom' : 'is-open'}`}>{general.text}</span>
                            </Tooltip>
                        </span>
                    </div>
                )}

                {!inherit && (
                    <div className="ss-wk-options" role="radiogroup" aria-label={`${row.day} choice`}>
                        {OPTIONS.map(option => {
                            const active = row.status === option.value;
                            return (
                                <button key={option.value} type="button" role="radio" aria-checked={active} disabled={disabled}
                                    className={`ss-wk-option is-${option.value} ${active ? 'active' : ''}`}
                                    onClick={() => updateRow(row.day, { status: option.value })}>
                                    <span className="ss-wk-option-icon">{option.icon}</span>
                                    <span className="ss-wk-option-title">{option.title}</span>
                                    <span className="ss-wk-option-hint">{option.hint}</span>
                                    {active && <span className="ss-wk-option-check"><FiCheck /></span>}
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="ss-wk-body">
                    <div className="ss-wk-details">
                        <div className="ss-wk-label">Details</div>
                        {row.status === DAY_STATUS.CUSTOM && (
                            <>
                                <div className="ss-wk-pair">
                                    <label className="ss-day-field" htmlFor={`${id}-open`}>
                                        <span>Opens at</span>
                                        <TimeField id={`${id}-open`} value={row.open} placeholder="Opening time" disabled={disabled}
                                            error={error} onChange={open => updateRow(row.day, { open })} />
                                    </label>
                                    <label className="ss-day-field" htmlFor={`${id}-close`}>
                                        <span>Closes at</span>
                                        <TimeField id={`${id}-close`} value={row.close} placeholder="Closing time" disabled={disabled}
                                            error={error} onChange={close => updateRow(row.day, { close })} />
                                    </label>
                                </div>
                                <div className="ss-wk-break">
                                    <SwitchField id={`${id}-break`} size="small" label="Add a break" checked={!!row.hasBreak} disabled={disabled}
                                        onChange={hasBreak => updateRow(row.day, { hasBreak })} />
                                    {row.hasBreak && (
                                        <div className="ss-wk-pair">
                                            <label className="ss-day-field" htmlFor={`${id}-break-from`}>
                                                <span>Break starts</span>
                                                <TimeField id={`${id}-break-from`} value={row.breakFrom} placeholder="Break start" disabled={disabled}
                                                    error={error} onChange={breakFrom => updateRow(row.day, { breakFrom })} />
                                            </label>
                                            <label className="ss-day-field" htmlFor={`${id}-break-to`}>
                                                <span>Break ends</span>
                                                <TimeField id={`${id}-break-to`} value={row.breakTo} placeholder="Break end" disabled={disabled}
                                                    error={error} onChange={breakTo => updateRow(row.day, { breakTo })} />
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        {row.status === DAY_STATUS.CUSTOM_OFF && (
                            <>
                                <div className="ss-wk-pair">
                                    <label className="ss-day-field" htmlFor={`${id}-from`}>
                                        <span>Closed from</span>
                                        <DateField id={`${id}-from`} value={row.from} placeholder="First closed date" disabled={disabled}
                                            disabledDate={isPastDate} error={error}
                                            onChange={from => updateRow(row.day, { from, to: row.to && row.to < from ? '' : row.to })} />
                                    </label>
                                    <label className="ss-day-field" htmlFor={`${id}-to`}>
                                        <span>Closed until</span>
                                        <DateField id={`${id}-to`} value={row.to} placeholder="Last closed date" disabled={disabled}
                                            disabledDate={date => isPastDate(date) || (row.from && date && date.isBefore(dayjs(row.from), 'day'))}
                                            error={error} onChange={to => updateRow(row.day, { to })} />
                                    </label>
                                </div>
                                {isRangeExpired(row) && <span className="ss-pill is-holiday">These dates have ended</span>}
                            </>
                        )}
                        {(row.status === DAY_STATUS.NORMAL || row.status === DAY_STATUS.OFF || inherit) && (
                            <span className="ss-wk-nothing">Nothing else to set for this choice.</span>
                        )}
                        {error && <span className="ss-field-error">{error}</span>}
                    </div>

                    <div className="ss-wk-result">
                        <div className="ss-wk-label">What customers get</div>
                        <DayTimeline rule={effectiveRule(row, general)} large />
                        <p className="ss-wk-sentence">{summaryFor(row, general)}</p>
                    </div>
                </div>

                {ranges.length > 0 && (
                    <div className="ss-day-saved">
                        <span className="ss-day-saved-label">Also saved for {row.day}</span>
                        {ranges.map(range => {
                            const rangeError = errors[rangeKey(row.day, range)];
                            return (
                                <Tooltip key={range.id} title={rangeError || 'Saved earlier. Kept when you save. Remove with ×.'}>
                                    <span className={`ss-pill ${rangeError ? 'is-holiday' : 'is-muted'}`}>
                                        {rangeLabel(range)}
                                        <button type="button" className="ss-pill-remove" aria-label={`Remove ${rangeLabel(range)}`}
                                            disabled={disabled} onClick={() => removeRange(row, range.id)}>
                                            <FiX />
                                        </button>
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </div>
                )}

                {/* Copy this day's setting to other days */}
                <div className="ss-wk-copy">
                    <span className="ss-wk-copy-label"><FiCopy /> Copy {row.day}'s setting to</span>
                    <div className="ss-wk-copy-days">
                        {DAYS.filter(day => day !== row.day).map(day => (
                            <button key={day} type="button" disabled={disabled}
                                className={`ss-chip ${copyTargets.includes(day) ? 'active' : ''}`}
                                aria-pressed={copyTargets.includes(day)}
                                onClick={() => toggleTarget(day)}>
                                {day.substring(0, 3)}
                            </button>
                        ))}
                        <button type="button" className="ss-link-btn" disabled={disabled}
                            onClick={() => setCopyTargets(WEEKDAYS.filter(day => day !== row.day))}>Weekdays</button>
                        <button type="button" className="ss-link-btn" disabled={disabled}
                            onClick={() => setCopyTargets(DAYS.filter(day => day !== row.day))}>All days</button>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled || copyTargets.length === 0} onClick={applyCopy}>
                        Copy{copyTargets.length ? ` to ${copyTargets.length} day${copyTargets.length > 1 ? 's' : ''}` : ''}
                    </button>
                    {copiedTo.length > 0 && (
                        <span className="ss-wk-copied"><FiCheck /> Copied to {copiedTo.map(day => day.substring(0, 3)).join(', ')}. Save changes to keep it.</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WeeklyScheduleEditor;
